/**
 * Composable for physics-based 360° viewer interactions
 * Uses constant deceleration momentum for frame-rate independent settling.
 * Prediction and animation share the exact same math for accuracy.
 */
export default (
  totalImages: number = 36,
  initialIndex: number = 0,
  sensitivity: number = 5,
  isTouchDevice: MaybeRefOrGetter<boolean> = ref(false),
) => {
  // State
  const currentImageIndex = ref(initialIndex);
  const isDragging = ref(false);
  const hasDragged = ref(false);
  // Velocity in index-units per millisecond (idx/ms)
  const velocity = ref(0);
  // Last pointer x position
  const lastPointerX = ref(0);
  const animationFrameId = ref<number | null>(null);
  // Recent pointer samples to estimate velocity accurately (px over time)
  const recentPointerSamples = ref<Array<{ time: number; x: number }>>([]);
  // Accumulated fractional rotation during drag/momentum (in index units)
  let rotationRemainder = 0;
  // Prediction state (for consumers like image preloader)
  const predictedSettleIndexRef = ref<number | null>(null);
  const lastSettledIndex = ref<number | null>(null);
  const stableIndex = ref<number | null>(null);
  let stableTimeout: ReturnType<typeof setTimeout> | null = null;

  // Constants for physics behavior
  const VELOCITY_WINDOW = 90; // ms window for velocity estimation
  const ROTATION_THRESHOLD = 1; // one unit == 1 frame index
  const TOUCH_SENSITIVITY = 12; // pixels per index on touch
  // Momentum physics (constant deceleration in idx/ms^2)
  const DECELERATION = 0.001; // higher = shorter glide
  const MIN_VELOCITY_THRESHOLD = 0.001; // idx/ms below which we stop
  const MAX_VELOCITY_IDX_PER_MS = 0.15; // clamp flick speed to avoid long spins
  const MAX_DT = 20; // cap dt between frames (ms)
  // Drag/hold behavior
  const STABLE_VELOCITY_THRESHOLD = 0.0009; // idx/ms considered "still" for stable marking
  const STABLE_SETTLE_DELAY_MS = 120; // delay before marking stable while holding

  /**
   * Normalize image index to stay within bounds (0 to totalImages-1)
   */
  const normalizeIndex = (index: number): number => {
    return ((index % totalImages) + totalImages) % totalImages;
  };

  /**
   * Set image index to a specific value
   */
  const setImageIndex = (index: number) => {
    currentImageIndex.value = normalizeIndex(index);
  };

  /**
   * Stop any running animations
   */
  const stopAnimation = () => {
    if (animationFrameId.value !== null) {
      cancelAnimationFrame(animationFrameId.value);
      animationFrameId.value = null;
    }
  };

  /**
   * Predict the exact resting index using constant deceleration.
   * Uses initial velocity (idx/ms) and current fractional remainder.
   * Closed-form stopping distance: stoppingDistance = (initialVelocity² - thresholdVelocity²) / (2 * deceleration)
   */
  const predictSettleIndex = (
    startIndex: number,
    initialVelocity: number,
  ): number => {
    const motionDirection = Math.sign(initialVelocity) || 1;
    const initialSpeedAbs = Math.abs(initialVelocity);
    const stoppingDistance =
      initialSpeedAbs > MIN_VELOCITY_THRESHOLD
        ? (initialSpeedAbs * initialSpeedAbs -
            MIN_VELOCITY_THRESHOLD * MIN_VELOCITY_THRESHOLD) /
          (2 * DECELERATION)
        : 0; // in index units

    // Include current fractional remainder projected onto motion direction
    const projectedRemainder = rotationRemainder * motionDirection;
    const totalDistance = stoppingDistance + projectedRemainder;
    const steps = Math.max(0, Math.floor(totalDistance / ROTATION_THRESHOLD));
    const finalIndex = startIndex + motionDirection * steps;

    return normalizeIndex(finalIndex);
  };

  /**
   * Start momentum-based animation after drag ends
   */
  const startMomentumAnimation = () => {
    stopAnimation();
    const initialVelocityIdxPerMs = velocity.value; // idx/ms
    // Clamp the initial flick speed
    const clampedInitialVelocityIdxPerMs = Math.max(
      -MAX_VELOCITY_IDX_PER_MS,
      Math.min(MAX_VELOCITY_IDX_PER_MS, initialVelocityIdxPerMs),
    );
    // Predict exact settle index now
    const predicted = predictSettleIndex(
      currentImageIndex.value,
      clampedInitialVelocityIdxPerMs,
    );
    predictedSettleIndexRef.value = predicted;

    // Don't animate if velocity is too low
    if (Math.abs(clampedInitialVelocityIdxPerMs) < MIN_VELOCITY_THRESHOLD) {
      lastSettledIndex.value = currentImageIndex.value;

      return;
    }

    let lastAnimationTimestampMs = Date.now();
    let currentVelocityIdxPerMs = clampedInitialVelocityIdxPerMs; // idx/ms

    const animate = () => {
      const nowMs = Date.now();
      const deltaTimeMs = Math.min(nowMs - lastAnimationTimestampMs, MAX_DT);
      lastAnimationTimestampMs = nowMs;

      const motionDirection = Math.sign(currentVelocityIdxPerMs);
      const velocityDelta = DECELERATION * deltaTimeMs;

      // Distance traveled during this frame with constant deceleration
      const currentSpeedAbs = Math.abs(currentVelocityIdxPerMs);
      const frameDistanceIdxUnits =
        currentSpeedAbs > 0
          ? currentVelocityIdxPerMs * deltaTimeMs -
            motionDirection * 0.5 * DECELERATION * deltaTimeMs * deltaTimeMs
          : 0;

      rotationRemainder += frameDistanceIdxUnits;

      // Apply movement to indices
      while (rotationRemainder >= ROTATION_THRESHOLD) {
        rotationRemainder -= ROTATION_THRESHOLD;
        setImageIndex(currentImageIndex.value + 1);
      }
      while (rotationRemainder <= -ROTATION_THRESHOLD) {
        rotationRemainder += ROTATION_THRESHOLD;
        setImageIndex(currentImageIndex.value - 1);
      }

      // Update velocity towards zero
      const newSpeedAbs = Math.max(0, currentSpeedAbs - velocityDelta);
      currentVelocityIdxPerMs = motionDirection * newSpeedAbs;

      // Stop when velocity is below threshold
      if (Math.abs(currentVelocityIdxPerMs) < MIN_VELOCITY_THRESHOLD) {
        stopAnimation();
        lastSettledIndex.value = currentImageIndex.value;

        return;
      }

      animationFrameId.value = requestAnimationFrame(animate);
    };

    animationFrameId.value = requestAnimationFrame(animate);
  };

  /**
   * Calculate velocity from recent drag movements
   */
  const calculateVelocity = (sensitivityPxPerIndex: number): number => {
    const nowMs = Date.now();
    // Keep only samples within velocity window
    const pointerSamples = recentPointerSamples.value.filter(
      (sample) => nowMs - sample.time <= VELOCITY_WINDOW,
    );

    if (pointerSamples.length < 2) return 0;

    const firstSample = pointerSamples[0]!;
    const lastSample = pointerSamples[pointerSamples.length - 1]!;
    const deltaTimeMs = lastSample.time - firstSample.time;
    if (deltaTimeMs <= 0) return 0;

    const deltaPixelsX = lastSample.x - firstSample.x;
    const velocityPixelsPerMs = deltaPixelsX / deltaTimeMs;

    // Convert to index-units per ms
    return velocityPixelsPerMs / sensitivityPxPerIndex;
  };

  /**
   * Start drag interaction
   */
  const startDrag = (clientX: number) => {
    stopAnimation();
    isDragging.value = true;
    hasDragged.value = false; // Reset drag state
    lastPointerX.value = clientX;
    velocity.value = 0;
    recentPointerSamples.value = [{ time: Date.now(), x: clientX }];
    rotationRemainder = 0; // reset fractional from previous momentum
    if (stableTimeout) {
      clearTimeout(stableTimeout);
      stableTimeout = null;
    }
    stableIndex.value = null;
    // If user holds without moving, mark current frame as stable after a short delay
    stableTimeout = setTimeout(() => {
      if (
        isDragging.value &&
        Math.abs(velocity.value) < STABLE_VELOCITY_THRESHOLD
      ) {
        stableIndex.value = currentImageIndex.value;
      }
    }, STABLE_SETTLE_DELAY_MS);
  };

  /**
   * Handle drag movement
   */
  const onDrag = (clientX: number) => {
    if (!isDragging.value) return;

    const nowMs = Date.now();
    const currentSensitivity = toValue(isTouchDevice)
      ? TOUCH_SENSITIVITY
      : sensitivity;

    // Track recent pointer positions
    recentPointerSamples.value.push({ time: nowMs, x: clientX });
    // Remove samples older than VELOCITY_WINDOW to maintain recent data for velocity calculation
    while (
      recentPointerSamples.value[0] &&
      nowMs - recentPointerSamples.value[0].time > VELOCITY_WINDOW
    ) {
      recentPointerSamples.value.shift();
    }

    velocity.value = calculateVelocity(currentSensitivity);

    // Update rotation based on pointer movement
    const deltaPixelsX = clientX - lastPointerX.value;
    lastPointerX.value = clientX;
    const deltaRotationIdxUnits = deltaPixelsX / currentSensitivity;
    rotationRemainder += deltaRotationIdxUnits;

    // Apply discrete index changes for whole units
    while (rotationRemainder >= ROTATION_THRESHOLD) {
      rotationRemainder -= ROTATION_THRESHOLD;
      setImageIndex(currentImageIndex.value + 1);
      hasDragged.value = true;
    }
    while (rotationRemainder <= -ROTATION_THRESHOLD) {
      rotationRemainder += ROTATION_THRESHOLD;
      setImageIndex(currentImageIndex.value - 1);
      hasDragged.value = true;
    }

    // Handle stationary hold (low velocity while dragging)
    const currentVelocityAbs = Math.abs(velocity.value);
    if (currentVelocityAbs >= STABLE_VELOCITY_THRESHOLD) {
      if (stableTimeout) {
        clearTimeout(stableTimeout);
        stableTimeout = null;
      }
    } else if (isDragging.value && !stableTimeout) {
      const indexSnapshot = currentImageIndex.value;
      stableTimeout = setTimeout(() => {
        stableIndex.value = indexSnapshot;
      }, STABLE_SETTLE_DELAY_MS);
    }
  };

  /**
   * End drag interaction and start momentum if applicable
   */
  const endDrag = () => {
    if (!isDragging.value) return;

    isDragging.value = false;
    const currentSensitivity = toValue(isTouchDevice)
      ? TOUCH_SENSITIVITY
      : sensitivity;
    velocity.value = calculateVelocity(currentSensitivity);
    recentPointerSamples.value = [];

    // Start momentum animation if velocity is significant
    if (Math.abs(velocity.value) > MIN_VELOCITY_THRESHOLD) {
      startMomentumAnimation();
    } else {
      lastSettledIndex.value = currentImageIndex.value;
      predictedSettleIndexRef.value = currentImageIndex.value;
    }

    if (stableTimeout) {
      clearTimeout(stableTimeout);
      stableTimeout = null;
    }
    stableIndex.value = null;
  };

  /**
   * Reset to initial state
   */
  const reset = () => {
    stopAnimation();
    currentImageIndex.value = initialIndex;
    isDragging.value = false;
    hasDragged.value = false;
    velocity.value = 0;
    recentPointerSamples.value = [];
    rotationRemainder = 0;
  };

  const isAnimating = computed(() => animationFrameId.value !== null);
  const canInteract = computed(() => !isAnimating.value || isDragging.value);

  onUnmounted(() => {
    stopAnimation();
    if (stableTimeout) clearTimeout(stableTimeout);
  });

  return {
    currentImageIndex,
    isDragging: computed(() => isDragging.value),
    hasDragged: computed(() => hasDragged.value),
    isAnimating,
    canInteract,
    velocity: computed(() => velocity.value),
    predictedSettleIndex: computed(() => predictedSettleIndexRef.value),
    settledIndex: computed(() => lastSettledIndex.value),
    stableIndex: computed(() => stableIndex.value),
    startDrag,
    onDrag,
    endDrag,
    setImageIndex,
    reset,
    stopAnimation,
    normalizeIndex,
  };
};
